/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import React from 'react';
import { SdsButtonProps } from '../Component.props';
import { TooltipProps } from '../Tooltip';
import { BUTTON_FORMAT_STRONG, ButtonFormat } from './types/ButtonFormat.types';
import { BUTTON_ICON_LEFT, ButtonIconPosition } from './types/ButtonIcon.types';
import { BUTTON_THEME_PRIMARY, ButtonTheme } from './types/ButtonTheme.types';
import { BUTTON_TYPE_BUTTON, ButtonType } from './types/ButtonType.types';

/**
 * The props for `<Button />`.
 */
export interface ButtonProps extends SdsButtonProps {
  /**
   * Custom click event handler.
   */
  customClick?(event: React.MouseEvent): void;

  /**
   * Custom content to override button content.
   */
  customContent?: React.ReactNode;

  /**
   * The custom data-id for the button.
   */
  dataId?: string;

  /**
   * Button format.
   */
  format?: ButtonFormat;

  /**
   * Button icon.
   */
  icon?: JSX.Element;

  /**
   * Which side of the button the icon is on.
   */
  iconPosition?: ButtonIconPosition;

  /**
   * Button icon colour from the sds colour map eg 'grey-300'.
   */
  iconColour?: string;

  /**
   * Button disabled state.
   */
  isDisabled?: boolean;

  /**
   * Button loading state.
   */
  isLoading?: boolean;

  /**
   * Button display mode.
   */
  isLite?: boolean;

  /**
   * Is the button rounded.
   */
  isRound?: boolean;

  /**
   * Whether the button is round and icon-only.
   */
  isRoundAndIconOnly?: boolean;

  /**
   * Button width.
   */
  isWide?: boolean;

  /**
   * Button accessible label.
   */
  label?: string;

  /**
   * Button text.
   */
  text?: string;

  /**
   * Button theme.
   */
  theme?: ButtonTheme;

  /**
   * Tooltip for the button
   */
  tooltip?: TooltipProps;

  /**
   * The default behavior of the button.
   */
  type?: ButtonType;
}

/**
 * The default prop values for `<Button />`.
 */
export const buttonDefaults: ButtonProps = {
  format: BUTTON_FORMAT_STRONG,
  iconPosition: BUTTON_ICON_LEFT,
  isLite: false,
  isWide: false,
  theme: BUTTON_THEME_PRIMARY,
  type: BUTTON_TYPE_BUTTON,
};
